import React, { forwardRef, useCallback } from 'react';
import { restoreV5RelativeTo, getRouterAdapter } from './utils';
import type { LeagueTo as To, LeagueLinkProps } from './router-types';

type Path = { pathname: string; search?: string; hash?: string };

export type LocationDescriptorObject<State = unknown> = Partial<Path> & {
  state?: State;
};

export type LocationDescriptor<State = unknown> =
  | string
  | LocationDescriptorObject<State>;

type LinkProps = Omit<LeagueLinkProps, 'to'> & { to: To | LocationDescriptor };

/**
 * Wrapper around react-router-dom's Link component that has two responsibilities:
 * 1. Ensures all `to`s are treated as absolute. Helps bridge between v5 APIs, which
 *    (more or less) treats all `to`s as absolute, and v6 APIs, which treats `to`s
 *    without a leading slash as relative.
 * 2. Retains support for having a `state` property in the `to` prop object type,
 *    in rr5 style. If one is provided, it will be used instead of the rr6-style `state` prop
 * 3. Internally calls `onPreventDefault` before any passed `onClick` handlers in the
 *    specific case that no `to` was provided, to prevent RRv6's new behavior of calling an
 *    internal `onClick` on `to`-less links where the passed `onClick` does not
 *    explicitly call `preventDefault()`.
 *
 * @deprecated use `Link` instead.`
 */
export const LinkLegacy = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to: maybeFunctionTo, onClick, ...rest }, ref) => {
    const to =
      typeof maybeFunctionTo === 'function'
        ? (maybeFunctionTo as Function)()
        : maybeFunctionTo;
    let { state } = rest;
    let actualTo: To;

    if (typeof to === 'object' && 'state' in to) {
      /**
       * this is an rr5-style `to` object, containing a `state` property.
       * we'll destructure it out and use it as the `state` prop.
       */
      ({ state, ...actualTo } = to);
    } else {
      actualTo = to;
    }

    const onClickWithPreventDefault = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        onClick!(event);
      },
      [onClick],
    );

    /**
     * If we have an `onClick` but no `to`, internally call `preventDefault()` before calling the passed
     * `onClick` just in case it did not do so already.
     */
    const resolvedOnClick =
      onClick && !to ? onClickWithPreventDefault : onClick;

    const { pathname } = getRouterAdapter().useLocation();
    const { Link } = getRouterAdapter();
    return (
      <Link
        relative="path"
        ref={ref}
        to={restoreV5RelativeTo(actualTo, pathname)}
        onClick={resolvedOnClick}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
        state={state}
      />
    );
  },
);
